/**
 * Iter 145 — Consolidated Fraud Alerts (Overview group).
 *
 * One page, two feeds: petty-cash + fuel. Locked behind the
 * `fraud_alerts.read` permission and `requires_role` MD/CEO/CMD/super_admin
 * gate in Sidebar.jsx, mirrored by the backend at /api/fraud-alerts.
 *
 * Visual style intentionally tracks the legacy
 * `pages/petty_cash/PettyCash.jsx::FraudAlerts` row layout so reviewers can
 * skim it the same way they skim the per-module feed.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, X, Filter } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, api } from '../../lib/api';
import { formatINR, formatDateTime } from '../../lib/format';

const MODULE_OPTIONS = [
  { value: '', label: 'All modules' },
  { value: 'petty_cash', label: 'Petty Cash' },
  { value: 'fuel', label: 'Fuel' },
];
const STATUS_OPTIONS = ['', 'Open', 'Resolved'];

const MODULE_BADGE = {
  petty_cash: { label: 'Petty Cash', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  fuel: { label: 'Fuel', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const SEVERITY_BADGE = {
  high: 'bg-rose-100 text-rose-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-700',
};

const STATUS_BADGE = {
  Open: 'bg-amber-100 text-amber-700',
  Investigating: 'bg-sky-100 text-sky-700',
  Resolved: 'bg-emerald-100 text-emerald-700',
  'False Positive': 'bg-slate-100 text-slate-700',
};

function InvoiceThumb({ url, alt }) {
  // Iter 141 viewer — fetches the auth-gated image as a blob and renders
  // a click-to-enlarge lightbox. Works for both petty-cash invoices and
  // fuel pump-bill photos (any URL the row carries).
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    if (!url) { setFailed(true); return; }
    (async () => {
      try {
        const path = url.startsWith('/api') ? url.replace(/^\/api/, '') : url;
        const r = await api.get(path, { responseType: 'blob' });
        if (cancelled) return;
        const obj = URL.createObjectURL(r.data);
        revoke = obj;
        setSrc(obj);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [url]);
  if (failed) {
    return (
      <div className="w-16 h-16 rounded-sm border border-slate-200 bg-slate-50 text-[9px] text-slate-400 flex items-center justify-center text-center px-1">
        no photo
      </div>
    );
  }
  if (!src) {
    return (
      <div className="w-16 h-16 rounded-sm border border-slate-200 bg-slate-50 flex items-center justify-center">
        <Loader2 size={14} className="animate-spin text-slate-300" />
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block w-16 h-16 rounded-sm overflow-hidden border border-slate-200 hover:border-[#3b4872] focus:outline-none focus:ring-2 focus:ring-[#3b4872] bg-slate-50"
        title="Click to enlarge the bill"
        data-testid="overview-alert-thumb"
      >
        <img src={src} alt={alt || 'Invoice'} className="w-full h-full object-cover" />
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setOpen(false)}
          data-testid="overview-alert-modal"
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute -top-3 -right-3 bg-white text-slate-800 rounded-full p-1.5 shadow hover:bg-slate-100"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <img src={src} alt={alt || 'Invoice'} className="w-full max-h-[88vh] object-contain rounded-sm bg-white" />
          </div>
        </div>
      )}
    </>
  );
}

export default function OverviewFraudAlerts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const params = [];
      if (moduleFilter) params.push(`module=${encodeURIComponent(moduleFilter)}`);
      if (statusFilter) params.push(`status=${encodeURIComponent(statusFilter)}`);
      const qs = params.length ? `?${params.join('&')}` : '';
      const d = await apiGet(`/fraud-alerts${qs}`);
      setRows(d?.items || []);
    } catch (e) {
      // 403 = no permission; show nothing rather than crash
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [moduleFilter, statusFilter]);

  const update = async (alert, status) => {
    // Optimistic update — patch state immediately so UI responds instantly,
    // no full list reload needed.
    setRows(prev => prev.map(r => r.id === alert.id ? { ...r, status } : r));
    try {
      const path = alert.module === 'fuel'
        ? `/fuel/alerts/${alert.id}`
        : `/petty-cash/alerts/${alert.id}`;
      await apiPut(path, { status });
      toast.success(`Marked ${status}`);
      // Quietly re-sync this single record to pick up any server-side changes
      const updated = await apiGet(alert.module === 'fuel' ? `/fuel/alerts/${alert.id}` : `/petty-cash/alerts/${alert.id}`).catch(() => null);
      if (updated) setRows(prev => prev.map(r => r.id === alert.id ? { ...r, ...updated } : r));
    } catch {
      // Roll back optimistic update
      setRows(prev => prev.map(r => r.id === alert.id ? { ...r, status: alert.status } : r));
      toast.error('Update failed — check your permissions.');
    }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length, open: 0, resolved: 0 };
    rows.forEach((r) => {
      if (r.status !== 'Resolved') c.open += 1;
      else c.resolved += 1;
    });
    return c;
  }, [rows]);

  return (
    <div className="p-6 space-y-4" data-testid="overview-fraud-alerts">
      <PageHeader
        title="Fraud Alerts"
        subtitle="One feed for petty-cash + fuel red flags awaiting executive review."
        icon={<AlertTriangle size={18} />}
      />

      {/* Filter strip */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <Filter size={14} className="text-slate-400" />
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white"
          data-testid="overview-fa-module-filter"
        >
          {MODULE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white"
          data-testid="overview-fa-status-filter"
        >
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || 'All statuses'}</option>)}
        </select>
        <div className="ml-auto flex gap-3 text-[11px] text-slate-500">
          <span><b className="text-slate-800">{counts.total}</b> total</span>
          <span className="text-amber-700"><b>{counts.open}</b> open</span>
          <span className="text-emerald-700"><b>{counts.resolved}</b> resolved</span>
        </div>
      </div>

      {/* Alert list */}
      <div className="space-y-2" data-testid="overview-fa-list">
        {loading && (
          <div className="text-center py-10 text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading alerts…
          </div>
        )}
        {!loading && rows.map((a) => {
          const modBadge = MODULE_BADGE[a.module] || { label: a.module || '—', cls: 'bg-slate-100 text-slate-700' };
          const sevCls = SEVERITY_BADGE[(a.severity || '').toLowerCase()] || 'bg-slate-100 text-slate-700';
          const stsCls = STATUS_BADGE[a.status] || 'bg-slate-100 text-slate-700';
          // Pump bill photo for fuel alerts, invoice image for petty-cash.
          const thumbUrl = a.invoice_image_url || a.photo_url || null;
          const altLabel = `${modBadge.label} alert — ${a.type || 'check'}`;
          return (
            <div
              key={`${a.module}-${a.id}`}
              className="bg-white border border-slate-200 rounded-sm p-3 flex items-start gap-3"
            >
              <InvoiceThumb url={thumbUrl} alt={altLabel} />
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider border ${modBadge.cls}`}>
                    {modBadge.label}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${sevCls}`}>
                    {a.severity || '—'}
                  </span>
                  <span className="font-display font-bold text-sm text-slate-800">
                    {(a.type || '').replaceAll('_', ' ') || '—'}
                  </span>
                </div>
                <div className="text-xs text-slate-600 leading-snug">{a.explanation || '—'}</div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500 mt-0.5">
                  <span>by <b className="text-slate-700">{a.actor || '—'}</b></span>
                  <span>· <b className="text-slate-700">{a.subject || '—'}</b></span>
                  {a.amount ? <span>· {formatINR(a.amount)}</span> : null}
                  {a.project_name ? <span>· {a.project_name}</span> : null}
                  <span>· {formatDateTime(a.created_at)}</span>
                  {typeof a.ai_confidence === 'number' && <span>· AI {a.ai_confidence}%</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${stsCls}`}>
                  {a.status || 'Open'}
                </span>
                {/* Only one action needed — Resolve closes the alert */}
                {a.status !== 'Resolved' && (
                  <button
                    onClick={() => update(a, 'Resolved')}
                    className="text-[10px] px-2 py-0.5 bg-emerald-600 text-white rounded-sm hover:bg-emerald-700"
                    data-testid="overview-fa-resolve"
                  >Resolve</button>
                )}
              </div>
            </div>
          );
        })}
        {!loading && !rows.length && (
          <div className="text-center text-slate-400 italic py-10 text-sm">
            No alerts firing — your books are clean.
          </div>
        )}
      </div>
    </div>
  );
}
